import React from 'react';
import AddressSearch from './AddressSearch';
import { EventModel } from './useEventManagement';
import {
  EVENT_TYPES, PRIVACY_LEVELS, SKILL_LEVELS, RIDING_STYLES, RIDING_STYLE_VALUES,
  ROUTE_TYPES, LANE_FILTERING, MOTORCYCLE_MAKES, CHAT_START_OPTIONS, STATUS_OPTIONS,
} from './eventTypes';
import { getStatusLabel } from './eventHelpers';

export const FORM_STEPS = ['Basics', 'Location', 'Ride Settings', 'Advanced', 'Review'];

const inputClass = "w-full px-3 py-2 rounded-lg text-sm border border-white/10 focus:border-blue-400/50 focus:outline-none transition-colors";
const inputStyle = { backgroundColor: 'var(--rp-surface-secondary)', color: 'var(--rp-text)' };
const labelClass = "block text-xs font-semibold mb-1";
const labelStyle = { color: 'var(--rp-text-secondary)' };

export function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  const id = React.useId();
  const child = React.isValidElement(children) &&
    (children.type === 'input' || children.type === 'select' || children.type === 'textarea')
    ? React.cloneElement(children as React.ReactElement, { id })
    : children;
  return (
    <div>
      <label htmlFor={id} className={labelClass} style={labelStyle}>{label}</label>
      {child}
    </div>
  );
}

export function SelectField({ label, value, options, onChange }: { label: string; value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) {
  return (
    <FormField label={label}>
      <select value={value} onChange={e => onChange(e.target.value)} className={inputClass} style={inputStyle} aria-label={label}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </FormField>
  );
}

const EventFormSteps: React.FC<{ model: EventModel }> = ({ model }) => {
  const {
    form, setForm, formStep, editingEvent, hostSearch, setHostSearch,
    hostOptions, setHostOptions, hostSearching, selectedHostName, setSelectedHostName,
  } = model;
  const renderFormField = (label: string, children: React.ReactNode) => (
    <FormField label={label}>{children}</FormField>
  );
  const renderSelectField = (label: string, value: string, options: { value: string; label: string }[], onChange: (v: string) => void) => (
    <SelectField label={label} value={value} options={options} onChange={onChange} />
  );
    switch (formStep) {
    case 0: // Basics
        return (
          <div className="space-y-4">
            {/* Host selection - only for new events */}
            {!editingEvent && (
              <div>
                <label htmlFor="event-host-search" className={labelClass} style={labelStyle}>Host *</label>
                <div className="relative">
                  <input
                    id="event-host-search"
                    type="text"
                    value={hostSearch}
                    onChange={e => { setHostSearch(e.target.value); setForm(f => ({ ...f, host_id: '' })); setSelectedHostName(''); }}
                    placeholder="Search by name or email..."
                    className={inputClass}
                    style={inputStyle}
                  />
                  {selectedHostName && (
                    <div className="mt-1 text-xs text-green-400">Selected: {selectedHostName}</div>
                  )}
                  {hostSearching && <div className="mt-1 text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>Searching...</div>}
                  {hostOptions.length > 0 && !form.host_id && (
                    <div className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto rounded-lg border border-white/10" style={{ backgroundColor: 'var(--rp-surface)' }}>
                      {hostOptions.map(h => (
                        <button
                          key={h.id}
                          onClick={() => {
                            setForm(f => ({ ...f, host_id: h.id }));
                            setSelectedHostName(h.name || h.email);
                            setHostSearch(h.name || h.email);
                            setHostOptions([]);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 transition-colors"
                          style={{ color: 'var(--rp-text)' }}
                        >
                          <div>{h.name || 'No name'}</div>
                          <div className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>{h.email}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {renderFormField('Title *',
              <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Event title" className={inputClass} style={inputStyle} />
            )}

            {renderFormField('Description',
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Event description..." rows={3} className={inputClass} style={inputStyle} />
            )}

            <div className="grid grid-cols-2 gap-4">
              {renderSelectField('Event Type', form.event_type, EVENT_TYPES.map(t => ({ value: t.value, label: t.label })), v => setForm(f => ({ ...f, event_type: v })))}
              {renderSelectField('Privacy', form.privacy_level, PRIVACY_LEVELS, v => setForm(f => ({ ...f, privacy_level: v })))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              {renderFormField('Meetup Time *',
                <input type="datetime-local" value={form.meetup_time} onChange={e => setForm(f => ({ ...f, meetup_time: e.target.value }))}
                  className={inputClass} style={inputStyle} />
              )}
              {renderFormField('Kick Stands Up Time',
                <input type="datetime-local" value={form.kick_stands_up_time} onChange={e => setForm(f => ({ ...f, kick_stands_up_time: e.target.value }))}
                  className={inputClass} style={inputStyle} />
              )}
            </div>

            {renderSelectField('Status', form.status, STATUS_OPTIONS.map(s => ({ value: s.value, label: s.label })), v => setForm(f => ({ ...f, status: v })))}
          </div>
        );

    case 1: // Location
        return (
          <div className="space-y-4">
            {renderFormField('Meetup Location',
              <AddressSearch
                value={form.location_name || form.address}
                onSelect={(place) => setForm(f => ({
                  ...f,
                  location_name: place.name,
                  address: place.address,
                  city: place.city,
                  state: place.state,
                  latitude: place.latitude.toString(),
                  longitude: place.longitude.toString(),
                }))}
                placeholder="Search for a meetup spot..."
                inputClass={inputClass}
                inputStyle={inputStyle}
              />
            )}
            {(form.location_name || form.city) && (
              <div className="rounded-lg p-3 border border-white/10 text-sm space-y-1" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                {form.location_name && <div style={{ color: 'var(--rp-text)' }}><strong>{form.location_name}</strong></div>}
                {form.address && <div style={{ color: 'var(--rp-text-secondary)' }}>{form.address}</div>}
                {(form.city || form.state) && (
                  <div style={{ color: 'var(--rp-text-tertiary)' }}>{[form.city, form.state].filter(Boolean).join(', ')}</div>
                )}
                {form.latitude && form.longitude && (
                  <div className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>Coordinates: {parseFloat(form.latitude).toFixed(5)}, {parseFloat(form.longitude).toFixed(5)}</div>
                )}
              </div>
            )}
            {renderFormField('Location Name (override)',
              <input type="text" value={form.location_name} onChange={e => setForm(f => ({ ...f, location_name: e.target.value }))}
                placeholder="Custom name (e.g. Starbucks on Main St)" className={inputClass} style={inputStyle} />
            )}

            <div className="border-t border-white/10 pt-4 mt-4">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--rp-text)' }}>Destination (optional)</h4>
              <AddressSearch
                value={form.gps_end_latitude && form.gps_end_longitude ? `${form.gps_end_latitude}, ${form.gps_end_longitude}` : ''}
                onSelect={(place) => setForm(f => ({
                  ...f,
                  gps_end_latitude: place.latitude.toString(),
                  gps_end_longitude: place.longitude.toString(),
                }))}
                placeholder="Search for destination..."
                inputClass={inputClass}
                inputStyle={inputStyle}
              />
              {form.gps_end_latitude && form.gps_end_longitude && (
                <div className="mt-2 rounded-lg p-2 border border-white/10 text-xs flex items-center justify-between" style={{ backgroundColor: 'var(--rp-surface-secondary)', color: 'var(--rp-text-tertiary)' }}>
                  <span>Coordinates: {parseFloat(form.gps_end_latitude).toFixed(5)}, {parseFloat(form.gps_end_longitude).toFixed(5)}</span>
                  <button onClick={() => setForm(f => ({ ...f, gps_end_latitude: '', gps_end_longitude: '' }))}
                    className="text-red-400 hover:text-red-300 ml-2">Clear</button>
                </div>
              )}
            </div>
          </div>
        );

    case 2: // Ride Settings
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {renderSelectField('Skill Level', form.skill_level, SKILL_LEVELS, v => setForm(f => ({ ...f, skill_level: v })))}
              {renderSelectField('Riding Style', form.riding_style, RIDING_STYLES, v => setForm(f => ({ ...f, riding_style: v })))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {renderSelectField('Pace Min', form.riding_style_min, RIDING_STYLE_VALUES, v => setForm(f => ({ ...f, riding_style_min: v })))}
              {renderSelectField('Pace Max', form.riding_style_max, RIDING_STYLE_VALUES, v => setForm(f => ({ ...f, riding_style_max: v })))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {renderSelectField('Route Type', form.route_type, ROUTE_TYPES, v => setForm(f => ({ ...f, route_type: v })))}
              {renderSelectField('Lane Filtering', form.lane_filtering, LANE_FILTERING, v => setForm(f => ({ ...f, lane_filtering: v })))}
            </div>
            {renderFormField('Max Participants',
              <input type="number" min="0" value={form.max_participants} onChange={e => setForm(f => ({ ...f, max_participants: e.target.value }))}
                placeholder="Unlimited" className={inputClass} style={inputStyle} />
            )}
            {renderSelectField('Gender Restriction', form.allowed_gender,
              [{ value: '', label: 'All Welcome' }, { value: 'male', label: 'Male Only' }, { value: 'female', label: 'Female Only' }],
              v => setForm(f => ({ ...f, allowed_gender: v })))}

            <div className="border-t border-white/10 pt-4 mt-4">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--rp-text)' }}>Motorcycle Restrictions</h4>
              <p className="text-xs mb-3" style={{ color: 'var(--rp-text-tertiary)' }}>Leave blank for no restrictions. Only riders matching these criteria can join.</p>

              <div className="space-y-3">
                <div>
                  <label className={labelClass} style={labelStyle}>Allowed Makes</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {form.motorcycle_restrictions.makes.map(make => (
                      <span key={make} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border border-orange-400/30 bg-orange-500/10 text-orange-300">
                        {make}
                        <button onClick={() => setForm(f => ({
                          ...f,
                          motorcycle_restrictions: { ...f.motorcycle_restrictions, makes: f.motorcycle_restrictions.makes.filter(m => m !== make) }
                        }))} className="hover:text-red-400">&times;</button>
                      </span>
                    ))}
                  </div>
                  <select
                    value=""
                    onChange={e => {
                      if (e.target.value && !form.motorcycle_restrictions.makes.includes(e.target.value)) {
                        setForm(f => ({
                          ...f,
                          motorcycle_restrictions: { ...f.motorcycle_restrictions, makes: [...f.motorcycle_restrictions.makes, e.target.value] }
                        }));
                      }
                    }}
                    className={inputClass} style={inputStyle}
                  >
                    <option value="">+ Add a make...</option>
                    {MOTORCYCLE_MAKES.filter(m => !form.motorcycle_restrictions.makes.includes(m)).map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {renderFormField('Min Year',
                    <input type="number" min="1900" max="2030" value={form.motorcycle_restrictions.yearMin}
                      onChange={e => setForm(f => ({ ...f, motorcycle_restrictions: { ...f.motorcycle_restrictions, yearMin: e.target.value } }))}
                      placeholder="e.g. 2015" className={inputClass} style={inputStyle} />
                  )}
                  {renderFormField('Max Year',
                    <input type="number" min="1900" max="2030" value={form.motorcycle_restrictions.yearMax}
                      onChange={e => setForm(f => ({ ...f, motorcycle_restrictions: { ...f.motorcycle_restrictions, yearMax: e.target.value } }))}
                      placeholder="e.g. 2026" className={inputClass} style={inputStyle} />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {renderFormField('Min Displacement (cc)',
                    <input type="number" min="0" value={form.motorcycle_restrictions.displacementMin}
                      onChange={e => setForm(f => ({ ...f, motorcycle_restrictions: { ...f.motorcycle_restrictions, displacementMin: e.target.value } }))}
                      placeholder="e.g. 500" className={inputClass} style={inputStyle} />
                  )}
                  {renderFormField('Max Displacement (cc)',
                    <input type="number" min="0" value={form.motorcycle_restrictions.displacementMax}
                      onChange={e => setForm(f => ({ ...f, motorcycle_restrictions: { ...f.motorcycle_restrictions, displacementMax: e.target.value } }))}
                      placeholder="e.g. 1800" className={inputClass} style={inputStyle} />
                  )}
                </div>
              </div>
            </div>
          </div>
        );

    case 3: // Advanced
        return (
          <div className="space-y-4">
            {renderFormField('Host Notes',
              <textarea value={form.host_notes} onChange={e => setForm(f => ({ ...f, host_notes: e.target.value }))}
                placeholder="Notes for participants..." rows={3} className={inputClass} style={inputStyle} />
            )}
            {renderFormField('Participant Rules',
              <textarea value={form.participant_rules} onChange={e => setForm(f => ({ ...f, participant_rules: e.target.value }))}
                placeholder="Rules for participants..." rows={3} className={inputClass} style={inputStyle} />
            )}
            {renderSelectField('Chat Room Opens', form.chat_room_start_time, CHAT_START_OPTIONS, v => setForm(f => ({ ...f, chat_room_start_time: v })))}

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.enable_route_sharing}
                  onChange={e => setForm(f => ({ ...f, enable_route_sharing: e.target.checked }))}
                  className="rounded border-white/20" />
                <span className="text-sm" style={{ color: 'var(--rp-text)' }}>Enable Route Sharing</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.chat_locked}
                  onChange={e => setForm(f => ({ ...f, chat_locked: e.target.checked }))}
                  className="rounded border-white/20" />
                <span className="text-sm" style={{ color: 'var(--rp-text)' }}>Lock Chat (host-only posting)</span>
              </label>
            </div>

            {form.privacy_level === 'invite_only' && (
              renderFormField('Invite Code',
                <input type="text" value={form.invite_code} onChange={e => setForm(f => ({ ...f, invite_code: e.target.value }))}
                  placeholder="6-character code" maxLength={6} className={inputClass} style={inputStyle} />
              )
            )}
          </div>
        );

    case 4: // Review
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span style={{ color: 'var(--rp-text-secondary)' }}>Title:</span> <span style={{ color: 'var(--rp-text)' }}>{form.title || '—'}</span></div>
              <div><span style={{ color: 'var(--rp-text-secondary)' }}>Type:</span> <span style={{ color: 'var(--rp-text)' }}>{EVENT_TYPES.find(t => t.value === form.event_type)?.label}</span></div>
              <div><span style={{ color: 'var(--rp-text-secondary)' }}>Status:</span> <span style={{ color: 'var(--rp-text)' }}>{getStatusLabel(form.status)}</span></div>
              <div><span style={{ color: 'var(--rp-text-secondary)' }}>Privacy:</span> <span style={{ color: 'var(--rp-text)' }}>{PRIVACY_LEVELS.find(p => p.value === form.privacy_level)?.label}</span></div>
              <div><span style={{ color: 'var(--rp-text-secondary)' }}>Meetup:</span> <span style={{ color: 'var(--rp-text)' }}>{form.meetup_time ? new Date(form.meetup_time).toLocaleString() : '—'}</span></div>
              <div><span style={{ color: 'var(--rp-text-secondary)' }}>KSU:</span> <span style={{ color: 'var(--rp-text)' }}>{form.kick_stands_up_time ? new Date(form.kick_stands_up_time).toLocaleString() : '—'}</span></div>
              <div><span style={{ color: 'var(--rp-text-secondary)' }}>Location:</span> <span style={{ color: 'var(--rp-text)' }}>{form.location_name || '—'}</span></div>
              <div><span style={{ color: 'var(--rp-text-secondary)' }}>City:</span> <span style={{ color: 'var(--rp-text)' }}>{form.city && form.state ? `${form.city}, ${form.state}` : '—'}</span></div>
              {selectedHostName && <div><span style={{ color: 'var(--rp-text-secondary)' }}>Host:</span> <span style={{ color: 'var(--rp-text)' }}>{selectedHostName}</span></div>}
              {form.skill_level && <div><span style={{ color: 'var(--rp-text-secondary)' }}>Skill:</span> <span style={{ color: 'var(--rp-text)' }}>{form.skill_level}</span></div>}
              {form.riding_style && <div><span style={{ color: 'var(--rp-text-secondary)' }}>Style:</span> <span style={{ color: 'var(--rp-text)' }}>{form.riding_style}</span></div>}
              {form.max_participants && <div><span style={{ color: 'var(--rp-text-secondary)' }}>Max:</span> <span style={{ color: 'var(--rp-text)' }}>{form.max_participants} riders</span></div>}
              {form.allowed_gender && <div><span style={{ color: 'var(--rp-text-secondary)' }}>Gender:</span> <span style={{ color: 'var(--rp-text)' }}>{form.allowed_gender === 'male' ? 'Male Only' : 'Female Only'}</span></div>}
            </div>
            {form.address && (
              <div className="text-sm">
                <span style={{ color: 'var(--rp-text-secondary)' }}>Address:</span>
                <p className="mt-1" style={{ color: 'var(--rp-text)' }}>{form.address}</p>
              </div>
            )}
            {form.description && (
              <div className="text-sm">
                <span style={{ color: 'var(--rp-text-secondary)' }}>Description:</span>
                <p className="mt-1" style={{ color: 'var(--rp-text)' }}>{form.description}</p>
              </div>
            )}
            {(form.motorcycle_restrictions.makes.length > 0 || form.motorcycle_restrictions.yearMin || form.motorcycle_restrictions.displacementMin) && (
              <div className="text-sm border-t border-white/10 pt-3">
                <span style={{ color: 'var(--rp-text-secondary)' }}>Motorcycle Restrictions:</span>
                <div className="mt-1 space-y-1" style={{ color: 'var(--rp-text)' }}>
                  {form.motorcycle_restrictions.makes.length > 0 && (
                    <div>Makes: {form.motorcycle_restrictions.makes.join(', ')}</div>
                  )}
                  {(form.motorcycle_restrictions.yearMin || form.motorcycle_restrictions.yearMax) && (
                    <div>Year: {form.motorcycle_restrictions.yearMin || '—'} – {form.motorcycle_restrictions.yearMax || '—'}</div>
                  )}
                  {(form.motorcycle_restrictions.displacementMin || form.motorcycle_restrictions.displacementMax) && (
                    <div>Displacement: {form.motorcycle_restrictions.displacementMin || '—'}cc – {form.motorcycle_restrictions.displacementMax || '—'}cc</div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

    default:
        return null;
    }
};

export default EventFormSteps;
